import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { HelpArticle, helpArticleService } from '@/services/helpArticleService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { RichTextEditor } from '@/components/email/RichTextEditor';

export default function HelpArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [formData, setFormData] = useState<Partial<HelpArticle>>({
    title: '',
    content: '',
    articleType: 'detailed',
    category: '',
    status: 'active',
    order: 0,
    shortDescription: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  // Fetch article if editing
  useEffect(() => {
    if (!isNew && id) {
      setIsLoading(true);
      helpArticleService
        .getArticleById(id)
        .then((response) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { page, ...articleData } = response.data as any;
          setFormData(articleData);
        })
        .catch((error) => {
          console.error('Error fetching article:', error);
          toast.error('Makale yüklenirken bir hata oluştu.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isNew]);

  // Handle form changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (html: string) => {
    setFormData((prev) => ({ ...prev, content: html }));
  };

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return await helpArticleService.createArticle(
          formData as Omit<
            HelpArticle,
            '_id' | 'createdAt' | 'updatedAt' | 'metadata' | 'feedbackStats'
          >
        );
      } else {
        return await helpArticleService.updateArticle(id!, formData);
      }
    },
    onSuccess: () => {
      toast.success(
        isNew
          ? 'Makale başarıyla oluşturuldu.'
          : 'Makale başarıyla güncellendi.'
      );
      navigate('/help-articles');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        'İşlem sırasında bir hata oluştu.';
      toast.error(message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await helpArticleService.deleteArticle(id!);
    },
    onSuccess: () => {
      toast.success('Makale başarıyla silindi.');
      navigate('/help-articles');
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Silme işlemi sırasında bir hata oluştu.';
      toast.error(message);
    },
  });

  const handleSave = () => {
    // Validation
    if (!formData.title?.trim()) {
      toast.error('Başlık zorunludur.');
      return;
    }
    if (!formData.content?.trim()) {
      toast.error('İçerik zorunludur.');
      return;
    }
    if (!formData.category?.trim()) {
      toast.error('Kategori zorunludur.');
      return;
    }

    saveMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/help-articles')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">
                {isNew ? 'Yeni Makale' : 'Makaleyi Düzenle'}
              </h1>
              <p className="text-muted-foreground">
                {isNew
                  ? 'Yeni bir yardım makalesi oluşturun'
                  : 'Mevcut makaleyi düzenleyin'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Sil
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Makaleyi sil?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Bu işlem geri alınamaz. Makale kalıcı olarak silinecektir.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>İptal</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteMutation.mutate()}
                      className="bg-destructive text-destructive-foreground"
                    >
                      Sil
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {saveMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Temel Bilgiler</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Makale başlığı"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label htmlFor="category">Kategori *</Label>
                  <Input
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="Örn: ÇALMA LİSTELERİ"
                  />
                </div>

                {/* Short Description */}
                <div className="space-y-2">
                  <Label htmlFor="shortDescription">
                    Kısa Açıklama (Detaylı makaleler için)
                  </Label>
                  <Input
                    id="shortDescription"
                    name="shortDescription"
                    value={formData.shortDescription || ''}
                    onChange={handleChange}
                    placeholder="Liste görünümünde gösterilecek kısa açıklama"
                  />
                </div>

                {/* Content */}
                <div className="space-y-2">
                  <Label>İçerik *</Label>
                  <RichTextEditor
                    content={formData.content || ''}
                    onChange={handleContentChange}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ayarlar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status */}
                <div className="space-y-2">
                  <Label>Durum *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      handleSelectChange('status', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktif</SelectItem>
                      <SelectItem value="inactive">Pasif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Order */}
                <div className="space-y-2">
                  <Label htmlFor="order">Sıralama</Label>
                  <Input
                    id="order"
                    name="order"
                    type="number"
                    value={formData.order}
                    onChange={handleChange}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">
                    Düşük sayılar önce gösterilir
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Feedback Stats (only for existing articles) */}
            {!isNew && formData.feedbackStats && (
              <Card>
                <CardHeader>
                  <CardTitle>Geri Bildirim İstatistikleri</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Faydalı:</span>
                    <Badge variant="default">
                      {formData.feedbackStats.helpful}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Faydalı Değil:
                    </span>
                    <Badge variant="secondary">
                      {formData.feedbackStats.notHelpful}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
